import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Wand2, Loader2, RefreshCw, Download, X, 
  Sparkles, Eye, Shirt
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { getStyleCode } from "@/data/styleCodes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SuitMockupPreview - A side panel component that generates AI mockups
 * of suits based on selected styling options.
 * 
 * @param {string} garmentType - Type of garment (e.g., "2-Piece Suit", "3-Piece Suit")
 * @param {object} jacketStyling - Jacket styling options
 * @param {object} pantStyling - Pant styling options
 * @param {object} vestStyling - Vest styling options
 * @param {object} shirtStyling - Shirt styling options
 * @param {string} fabricColor - Fabric color (optional)
 * @param {string} fabricPattern - Fabric pattern (optional)
 * @param {boolean} isExpanded - Whether the panel is expanded
 * @param {function} onToggle - Function to toggle the panel
 */
const SuitMockupPreview = ({
  garmentType = "2-Piece Suit",
  jacketStyling = {},
  pantStyling = {},
  vestStyling = {},
  shirtStyling = {},
  fabricColor = "Navy Blue",
  fabricPattern = "Solid",
  isExpanded = true,
  onToggle
}) => {
  const [mockupImage, setMockupImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);
  const containerRef = useRef(null);

  // Determine which garments are included
  const hasJacket = ["2-Piece Suit", "3-Piece Suit", "Sport Coat", "Overcoat", "Tuxedo"].includes(garmentType);
  const hasPants = ["2-Piece Suit", "3-Piece Suit", "Trousers", "Tuxedo"].includes(garmentType);
  const hasVest = ["3-Piece Suit", "Vest"].includes(garmentType);
  const hasShirt = ["Dress Shirt"].includes(garmentType);

  // Values that mean "user didn't really pick anything" — skip on the preview.
  const SKIP_VALUES = new Set([
    "", "Normal", "Standard", "None", "Default", "default", "—",
    "no", "No", "false", "False",
  ]);
  // Fields that are pure UI metadata / freeform notes, not stylable picks.
  const SKIP_FIELD_SUFFIXES = ["_remarks", "_notes", "_comment", "_meta"];
  const SKIP_FIELDS = new Set([
    "garment_type", "id", "_id", "createdAt", "updatedAt", "starting_specs",
    "starting_size", "tryon_size",
  ]);

  const isMeaningfulValue = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "object") return false; // nested measurements, etc.
    const s = String(v).trim();
    if (!s) return false;
    return !SKIP_VALUES.has(s);
  };

  const prettifyField = (key) => {
    return key
      .replace(/^pant_|^vest_|^shirt_|^jacket_/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .replace(/\bAdj\b/g, "Adj.")
      .replace(/\bL\b/g, "L")
      .replace(/\bR\b/g, "R");
  };

  // Collect every meaningful (label, value, code) tuple from a styling dict.
  // The factory code lookup uses the raw field key, so we keep it (e.g.
  // `pant_lining_color`) rather than the stripped pretty label.
  const collectPicks = (styling = {}) => {
    const out = [];
    if (!styling || typeof styling !== "object") return out;
    for (const [key, raw] of Object.entries(styling)) {
      if (SKIP_FIELDS.has(key)) continue;
      if (SKIP_FIELD_SUFFIXES.some((suf) => key.endsWith(suf))) continue;
      if (!isMeaningfulValue(raw)) continue;
      const value = String(raw);
      const code = getStyleCode(key, [], value) || "";
      out.push({
        key,
        label: prettifyField(key),
        value,
        code,
      });
    }
    return out;
  };

  // Build the full multi-garment summary used by the Style Preview panel.
  const getFullStyleSummary = () => {
    const sections = [];
    if (hasJacket) {
      const picks = collectPicks(jacketStyling);
      if (picks.length) sections.push({ label: garmentType === "Overcoat" ? "Overcoat" : "Jacket", picks });
    }
    if (hasPants) {
      const picks = collectPicks(pantStyling);
      if (picks.length) sections.push({ label: "Trousers", picks });
    }
    if (hasVest) {
      const picks = collectPicks(vestStyling);
      if (picks.length) sections.push({ label: "Vest", picks });
    }
    if (hasShirt) {
      const picks = collectPicks(shirtStyling);
      if (picks.length) sections.push({ label: "Shirt", picks });
    }
    return sections;
  };

  // Create a summary of current styling selections
  // Legacy 3-field summary kept for the AI mockup-generation prompt only.
  const getStyleSummary = () => {
    const summary = [];

    if (hasJacket && jacketStyling) {
      const jacketItems = [];
      if (jacketStyling.lapel_type) jacketItems.push(jacketStyling.lapel_type);
      if (jacketStyling.front_closure) jacketItems.push(jacketStyling.front_closure);
      if (jacketStyling.vent) jacketItems.push(jacketStyling.vent);
      if (jacketItems.length > 0) {
        summary.push({ label: "Jacket", items: jacketItems.join(", ") });
      }
    }

    if (hasPants && pantStyling) {
      const pantItems = [];
      if (pantStyling.front_style) pantItems.push(pantStyling.front_style);
      if (pantStyling.bottom_finishing) pantItems.push(pantStyling.bottom_finishing);
      if (pantItems.length > 0) {
        summary.push({ label: "Trousers", items: pantItems.join(", ") });
      }
    }

    if (hasVest && vestStyling) {
      const vestItems = [];
      if (vestStyling.lapel && vestStyling.lapel !== "None") vestItems.push(vestStyling.lapel + " Lapel");
      if (vestStyling.front_closure) vestItems.push(vestStyling.front_closure);
      if (vestItems.length > 0) {
        summary.push({ label: "Vest", items: vestItems.join(", ") });
      }
    }

    return summary;
  };

  const generateMockup = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/admin/suit-mockup/generate`, {
        garment_type: garmentType,
        fabric_color: fabricColor,
        fabric_pattern: fabricPattern,
        jacket_styling: hasJacket ? jacketStyling : null,
        pant_styling: hasPants ? pantStyling : null,
        vest_styling: hasVest ? vestStyling : null
      }, {
        timeout: 120000 // 2 minute timeout for image generation
      });
      
      if (response.data.success && response.data.image_base64) {
        setMockupImage({
          base64: response.data.image_base64,
          summary: response.data.prompt_summary
        });
        setLastGenerated(new Date());
        toast.success("Mockup generated!");
      }
    } catch (err) {
      console.error("Mockup generation error:", err);
      setError(err.response?.data?.detail || "Failed to generate mockup");
      toast.error("Failed to generate mockup");
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadMockup = () => {
    if (!mockupImage?.base64) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${mockupImage.base64}`;
    link.download = `suit_mockup_${garmentType.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.png`;
    link.click();
    toast.success("Image downloaded!");
  };

  const styleSections = getFullStyleSummary();
  const totalPicks = styleSections.reduce((sum, s) => sum + s.picks.length, 0);

  if (!isExpanded) {
    return (
      <button
        onClick={onToggle}
        className="fixed right-0 top-1/2 -translate-y-1/2 bg-gradient-to-l from-amber-500 to-orange-500 text-white px-2 py-4 rounded-l-lg shadow-lg hover:px-3 transition-all z-50"
        data-testid="expand-mockup-panel-btn"
      >
        <Eye size={20} />
        <span className="sr-only">Show Preview</span>
      </button>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      initial={{ x: 320 }}
      animate={{ x: 0 }}
      exit={{ x: 320 }}
      className="w-80 bg-card border-l border-white/10 h-full overflow-y-auto"
      data-testid="suit-mockup-preview-panel"
    >
      {/* Header */}
      <div className="sticky top-0 bg-card border-b border-white/10 p-4 z-10">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg flex items-center gap-2">
            <Sparkles size={18} className="text-amber-400" />
            Style Preview
          </h3>
          {onToggle && (
            <button
              onClick={onToggle}
              className="text-muted-foreground hover:text-foreground p-1"
              data-testid="collapse-mockup-panel-btn"
            >
              <X size={18} />
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          AI-generated mockup based on your selections
        </p>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Current Selections Summary */}
        <div
          className="bg-white/5 rounded-lg border border-white/10"
          data-testid="style-preview-selections"
        >
          <div className="flex items-center justify-between gap-2 px-3 pt-3">
            <div className="flex items-center gap-2">
              <Shirt size={14} className="text-amber-400" />
              <span className="text-xs font-medium text-amber-400">{garmentType}</span>
            </div>
            <span className="text-[10px] text-muted-foreground">
              {totalPicks} {totalPicks === 1 ? "pick" : "picks"}
            </span>
          </div>

          <div className="px-3 pt-2 pb-1">
            {fabricColor && (
              <p className="text-xs text-muted-foreground">
                <span className="text-foreground">Fabric:</span> {fabricColor}
                {fabricPattern && fabricPattern !== "Solid" && ` · ${fabricPattern}`}
              </p>
            )}
          </div>

          {styleSections.length === 0 && (
            <p className="px-3 pb-3 text-xs text-muted-foreground italic">
              No styling picks yet — make selections above and they will appear here.
            </p>
          )}

          {styleSections.map((section) => (
            <div
              key={section.label}
              className="border-t border-white/5 px-3 py-2"
              data-testid={`style-preview-section-${section.label.toLowerCase()}`}
            >
              <p className="text-[10px] uppercase tracking-wider text-amber-400/80 font-semibold mb-1">
                {section.label}
                <span className="ml-1 text-muted-foreground/70 normal-case tracking-normal">
                  · {section.picks.length}
                </span>
              </p>
              <div className="space-y-0.5">
                {section.picks.map((p) => (
                  <div
                    key={p.key}
                    className="flex items-baseline gap-1.5 text-[11px] leading-tight"
                    title={`${p.label}: ${p.value}${p.code ? " — " + p.code : ""}`}
                  >
                    <span className="text-muted-foreground shrink-0">{p.label}:</span>
                    <span className="text-foreground truncate flex-1">{p.value}</span>
                    {p.code && (
                      <span className="font-mono text-[9px] uppercase text-amber-400 bg-amber-500/10 px-1 py-0.5 rounded shrink-0">
                        {p.code}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Generate Button */}
        <Button
          onClick={generateMockup}
          disabled={isGenerating}
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
          data-testid="generate-mockup-btn"
        >
          {isGenerating ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Generating...
            </>
          ) : mockupImage ? (
            <>
              <RefreshCw size={16} className="mr-2" />
              Regenerate Preview
            </>
          ) : (
            <>
              <Wand2 size={16} className="mr-2" />
              Generate Preview
            </>
          )}
        </Button>

        {/* Error Message */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isGenerating && (
          <div className="bg-white/5 rounded-lg p-6 text-center border border-white/10">
            <div className="relative w-16 h-16 mx-auto mb-3">
              <div className="absolute inset-0 border-4 border-amber-500/20 rounded-full" />
              <div className="absolute inset-0 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
            <p className="text-sm text-muted-foreground">Creating your mockup...</p>
            <p className="text-xs text-muted-foreground mt-1">This may take 30-60 seconds</p>
          </div>
        )}

        {/* Mockup Image */}
        <AnimatePresence mode="wait">
          {mockupImage && !isGenerating && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="space-y-3"
            >
              {/* Image Container */}
              <div className="relative rounded-lg overflow-hidden bg-black/20 border border-amber-500/30">
                <img
                  src={`data:image/png;base64,${mockupImage.base64}`}
                  alt={`${garmentType} mockup preview`}
                  className="w-full h-auto"
                  data-testid="mockup-preview-image"
                />
                
                {/* Download Button Overlay */}
                <button
                  onClick={downloadMockup}
                  className="absolute top-2 right-2 p-2 bg-black/60 rounded-lg hover:bg-black/80 transition-colors"
                  title="Download mockup"
                  data-testid="download-mockup-btn"
                >
                  <Download size={16} className="text-white" />
                </button>
              </div>

              {/* Generation Info */}
              {lastGenerated && (
                <p className="text-xs text-center text-muted-foreground">
                  Generated {lastGenerated.toLocaleTimeString()}
                </p>
              )}

              {/* AI Note */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">
                  <span className="text-amber-400 font-medium">Note:</span> This is an AI-generated visualization. 
                  The final garment will be crafted by our tailors based on your exact specifications.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        {!mockupImage && !isGenerating && (
          <div className="bg-white/5 rounded-lg p-6 text-center border border-dashed border-white/10">
            <Wand2 size={32} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              Select your styling options and click Generate to see a preview
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SuitMockupPreview;
